// =============================================================================
// Layer Creation & Management Utilities
// =============================================================================

import type { CanvasLayer, CanvasLayerType, CanvasState, FillDef, TextStyle } from './types';
import { DEFAULT_TEXT_STYLE, DEFAULT_SHAPE_FILL } from './constants';

// -----------------------------------------------------------------------------
// Layer ID Generation
// -----------------------------------------------------------------------------

/** Generate a unique layer ID. Uses crypto.randomUUID where available. */
export function generateLayerId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// -----------------------------------------------------------------------------
// Layer Factory
// -----------------------------------------------------------------------------

type CreateLayerOptions = Omit<CanvasLayer, 'id' | 'z_index'>;

/** Create a new layer with defaults applied. */
export function createLayer(
  options: Partial<CreateLayerOptions> & { type: CanvasLayerType },
  zIndex: number
): CanvasLayer {
  return {
    id: generateLayerId(),
    name: options.name ?? getDefaultLayerName(options.type),
    type: options.type,
    visible: options.visible ?? true,
    locked: options.locked ?? false,
    x: options.x ?? 0,
    y: options.y ?? 0,
    width: options.width ?? 200,
    height: options.height ?? 200,
    rotation: options.rotation ?? 0,
    scaleX: options.scaleX ?? 1,
    scaleY: options.scaleY ?? 1,
    opacity: options.opacity ?? 1,
    z_index: zIndex,
    blendMode: options.blendMode ?? 'normal',
    fillOpacity: options.fillOpacity ?? 1,
    effects: options.effects ?? [],
    entity_tokens: options.entity_tokens ?? [],
    metadata: options.metadata,

    // Optional type-specific properties
    text_content: options.text_content,
    text_style: options.text_style,
    asset_id: options.asset_id,
    filters: options.filters,
    cropRect: options.cropRect,
    shapeType: options.shapeType,
    fill: options.fill,
    stroke: options.stroke,
    strokeWidth: options.strokeWidth,
    strokeDashArray: options.strokeDashArray,
    cornerRadius: options.cornerRadius,
    pathData: options.pathData,
    clipMask: options.clipMask,
    layerMask: options.layerMask,
    groupId: options.groupId,
    isGroup: options.isGroup,
    expanded: options.expanded,
  };
}

/** Create a text layer with sensible defaults. */
export function createTextLayer(
  text: string,
  canvasWidth: number,
  canvasHeight: number,
  style?: Partial<TextStyle>
): Partial<CreateLayerOptions> & { type: CanvasLayerType } {
  const textWidth = Math.min(canvasWidth * 0.8, 600);
  return {
    type: 'text',
    name: text.slice(0, 20) || 'Text',
    x: (canvasWidth - textWidth) / 2,
    y: canvasHeight / 2 - 30,
    width: textWidth,
    height: 60,
    text_content: text,
    text_style: { ...DEFAULT_TEXT_STYLE, ...style },
  };
}

/** Create an image layer. */
export function createImageLayer(
  assetId: string,
  x: number,
  y: number,
  width: number = 300,
  height: number = 300,
  name: string = 'Image'
): Partial<CreateLayerOptions> & { type: CanvasLayerType } {
  return {
    type: 'image',
    name,
    x,
    y,
    width,
    height,
    asset_id: assetId,
  };
}

/** Create a shape layer. */
export function createShapeLayer(
  shapeType: 'rectangle' | 'ellipse' | 'line' | 'polygon' = 'rectangle',
  x: number,
  y: number,
  width: number = 200,
  height: number = 200,
  fill?: FillDef
): Partial<CreateLayerOptions> & { type: CanvasLayerType } {
  return {
    type: 'shape',
    name: `${shapeType.charAt(0).toUpperCase() + shapeType.slice(1)}`,
    shapeType,
    x,
    y,
    width,
    height,
    fill: fill ?? DEFAULT_SHAPE_FILL,
    strokeWidth: 0,
    cornerRadius: 0,
  };
}

// -----------------------------------------------------------------------------
// Layer Name Helpers
// -----------------------------------------------------------------------------

function getDefaultLayerName(type: CanvasLayerType): string {
  switch (type) {
    case 'text': return 'Text';
    case 'image': return 'Image';
    case 'shape': return 'Shape';
    case 'background': return 'Background';
    case 'logo': return 'Logo';
    default: return 'Layer';
  }
}

// -----------------------------------------------------------------------------
// Z-Index Management
// -----------------------------------------------------------------------------

/** Reindex all layers sequentially by current order. */
export function reindexLayers(layers: CanvasLayer[]): CanvasLayer[] {
  return layers.map((layer, i) => ({ ...layer, z_index: i }));
}

/** Move a layer to a new z-index position. */
export function moveLayerToIndex(
  layers: CanvasLayer[],
  layerId: string,
  newIndex: number
): CanvasLayer[] {
  const arr = [...layers];
  const oldIndex = arr.findIndex((l) => l.id === layerId);
  if (oldIndex === -1) return layers;

  const [moved] = arr.splice(oldIndex, 1);
  arr.splice(newIndex, 0, moved);
  return reindexLayers(arr);
}

/** Bring layer one step forward. */
export function bringForward(layers: CanvasLayer[], layerId: string): CanvasLayer[] {
  const index = layers.findIndex((l) => l.id === layerId);
  if (index === -1 || index >= layers.length - 1) return layers;
  return moveLayerToIndex(layers, layerId, index + 1);
}

/** Send layer one step backward. */
export function sendBackward(layers: CanvasLayer[], layerId: string): CanvasLayer[] {
  const index = layers.findIndex((l) => l.id === layerId);
  if (index <= 1) return layers; // Don't go behind background (index 0)
  return moveLayerToIndex(layers, layerId, index - 1);
}

/** Bring layer to front (top of stack). */
export function bringToFront(layers: CanvasLayer[], layerId: string): CanvasLayer[] {
  return moveLayerToIndex(layers, layerId, layers.length - 1);
}

/** Send layer to back (just above background). */
export function sendToBack(layers: CanvasLayer[], layerId: string): CanvasLayer[] {
  return moveLayerToIndex(layers, layerId, 1); // Index 1, above background
}

// -----------------------------------------------------------------------------
// Layer Queries
// -----------------------------------------------------------------------------

/** Get a layer by ID. */
export function getLayerById(state: CanvasState, layerId: string): CanvasLayer | undefined {
  return state.layers.find((l) => l.id === layerId);
}

/** Get all layers sorted by z_index (bottom to top). */
export function getLayersSorted(state: CanvasState): CanvasLayer[] {
  return [...state.layers].sort((a, b) => a.z_index - b.z_index);
}

/** Get all visible layers. */
export function getVisibleLayers(state: CanvasState): CanvasLayer[] {
  return state.layers.filter((l) => l.visible);
}

/** Get layers in a group. */
export function getGroupChildren(state: CanvasState, groupId: string): CanvasLayer[] {
  return state.layers.filter((l) => l.groupId === groupId);
}

// -----------------------------------------------------------------------------
// Layer Duplication
// -----------------------------------------------------------------------------

/** Duplicate a layer with a new ID and slight offset. */
export function duplicateLayer(
  layer: CanvasLayer,
  newZIndex: number,
  offset: number = 20
): CanvasLayer {
  return {
    ...structuredClone(layer),
    id: generateLayerId(),
    name: `${layer.name} copy`,
    x: layer.x + offset,
    y: layer.y + offset,
    z_index: newZIndex,
  };
}

/** Duplicate multiple layers, preserving relative positions and group relationships. */
export function duplicateLayers(
  layers: CanvasLayer[],
  startZIndex: number,
  offset: number = 20
): CanvasLayer[] {
  const idMap = new Map<string, string>();

  // Generate new IDs first
  for (const layer of layers) {
    idMap.set(layer.id, generateLayerId());
  }

  return layers.map((layer, i) => ({
    ...structuredClone(layer),
    id: idMap.get(layer.id)!,
    name: `${layer.name} copy`,
    x: layer.x + offset,
    y: layer.y + offset,
    z_index: startZIndex + i,
    groupId: layer.groupId ? idMap.get(layer.groupId) ?? layer.groupId : undefined,
  }));
}
